import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Grid3X3, Plus, Pencil, Trash2, Loader2, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

export default function CategoriesAdminPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCat, setEditCat] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', description: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/catalog/categories').then(r => setCategories(r.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const slug = form.slug || slugify(form.name);
      await api.post('/admin/catalog/categories', { ...form, slug });
      toast.success('Category created');
      setShowCreate(false);
      setForm({ name: '', slug: '', description: '', sort_order: 0 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!editCat) return;
    setSaving(true);
    try {
      await api.put(`/admin/catalog/categories/${editCat._origSlug}`, form);
      toast.success('Category updated');
      setEditCat(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (cat) => {
    try {
      await api.put(`/admin/catalog/categories/${cat.slug}`, { is_active: !cat.is_active });
      toast.success(cat.is_active ? 'Deactivated' : 'Reactivated');
      load();
    } catch (err) { toast.error('Failed'); }
  };

  const handleDelete = async (cat) => {
    if (!confirm(`Delete "${cat.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/catalog/categories/${cat.slug}`);
      toast.success('Category deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot delete');
    }
  };

  const openEdit = (c) => {
    setForm({ name: c.name, slug: c.slug, description: c.description || '', sort_order: c.sort_order || 0, is_active: c.is_active });
    setEditCat({ ...c, _origSlug: c.slug });
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="categories-admin-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Grid3X3 className="h-4 w-4 text-ink" />
          <h1 className="text-base font-semibold text-ink">Category Management</h1>
          <Badge variant="outline" className="text-[10px]">{categories.length} categories</Badge>
        </div>
        <Button size="sm" className="gap-1" onClick={() => { setForm({ name: '', slug: '', description: '', sort_order: categories.length }); setShowCreate(true); }} data-testid="create-category-btn">
          <Plus className="h-3 w-3" /> Add Category
        </Button>
      </div>

      <Card className="bg-white border-stone/50">
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-stone/30">
                <th className="text-left p-3 font-medium text-muted-foreground"><ArrowUpDown className="h-3 w-3 inline mr-1" />Order</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Name</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Slug</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Items</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Rules</th>
                <th className="text-right p-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(c => (
                <tr key={c.id} className="border-b border-stone/10 hover:bg-stone/5">
                  <td className="p-3 text-muted-foreground font-mono">{c.sort_order}</td>
                  <td className="p-3 font-medium text-ink">{c.name}</td>
                  <td className="p-3 font-mono text-muted-foreground">{c.slug}</td>
                  <td className="p-3">
                    <Badge variant={c.is_active ? "default" : "secondary"} className="text-[9px]"
                           style={c.is_active ? {background:'rgba(14,107,93,0.10)',color:'#0E6B5D'} : {}}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="p-3 text-muted-foreground">{c.reference_count || 0}</td>
                  <td className="p-3 text-muted-foreground">{c.image_rule_count || 0}</td>
                  <td className="p-3 text-right space-x-1">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEdit(c)}><Pencil className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleToggleActive(c)}>
                      <Switch checked={c.is_active} className="scale-75" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => handleDelete(c)} disabled={c.reference_count > 0}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-sm">Create Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value, slug: slugify(e.target.value)})} className="mt-1" placeholder="Category name" data-testid="category-name-input" /></div>
            <div><Label className="text-xs">Slug</Label><Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} className="mt-1 font-mono" placeholder="category-slug" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1" rows={2} /></div>
            <div><Label className="text-xs">Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value)||0})} className="mt-1 w-20" /></div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={handleCreate} disabled={saving} data-testid="save-category-btn">{saving ? 'Creating...' : 'Create Category'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editCat} onOpenChange={() => setEditCat(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-sm">Edit Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1" /></div>
            <div><Label className="text-xs">Slug</Label><Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} className="mt-1 font-mono" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1" rows={2} /></div>
            <div><Label className="text-xs">Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value)||0})} className="mt-1 w-20" /></div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={handleUpdate} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
